import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import lm from "../../assets/launch.webp";

/* ================= ANIMATION VARIANTS ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.25,
    },
  },
};

const bgVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 1.2 },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const floatingCardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 14,
    },
  },
};

const pillVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

const headingVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.7 },
  },
};

const addressVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const planeVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6 },
  },
};

/* ================= COMPONENT ================= */

export default function Launch() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "-100px",
  });

  return (
    <section
      ref={ref}
      className="relative w-full min-h-[520px] overflow-hidden bg-white"
    >
      {/* BACKGROUND */}
      <motion.div
        variants={bgVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="absolute inset-0 bg-gradient-to-br from-[#0a2fb8] via-[#0b37d8] to-[#071f86] blue-cut-right"
      />

      {/* LEFT DECOR */}
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={{
          hidden: { opacity: 0, scale: 0.8 },
          visible: { opacity: 1, scale: 1, transition: { duration: 1 } },
        }}
        className="hidden lg:block absolute -left-40 top-24 w-[420px] h-[420px] bg-blue-600/15 rounded-full blur-3xl"
      />

      {/* CONTENT */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="relative max-w-[1300px] mx-auto min-h-[520px] grid grid-cols-1 lg:grid-cols-2 gap-14 px-6 lg:px-10 py-14 lg:py-0"
      >
        {/* ================= LEFT IMAGE ================= */}
        <motion.div
          variants={imageVariants}
          className="relative flex items-center justify-center lg:justify-start"
        >
          <div className="relative w-full max-w-[520px]">
            <div className="relative h-[240px] sm:h-[280px] lg:h-[330px] rounded-[28px] border-[3px] border-white">
              <div className="relative w-full h-full rounded-[24px] overflow-hidden bg-[#f3f6ff]">
                <img
                  src={lm}
                  alt="students"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* FLOATING CARD */}
              <motion.div
                variants={floatingCardVariants}
                className="absolute left-0 -bottom-8 -translate-x-1/2 bg-[#0a2fb8] px-8 py-6 rounded-2xl shadow-2xl flex items-center gap-5 z-30 min-w-[240px]"
              >
                <div className="w-14 h-14 rounded-full bg-white text-[#0a2fb8] flex items-center justify-center text-2xl">
                  ✓
                </div>
                <div className="text-[15px] leading-tight">
                  <p className="font-extrabold tracking-wide text-white">
                    DREAM
                  </p>
                  <p className="text-white font-semibold">BIG</p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* ================= RIGHT CONTENT ================= */}
        <div className="flex flex-col justify-center text-white text-center lg:text-left">
          {/* PILLS */}
          <motion.div className="flex flex-wrap justify-center lg:justify-start gap-3 mb-6">
            {["USA", "Hungary", "UK", "Malaysia"].map((c) => (
              <motion.div
                key={c}
                variants={pillVariants}
                className="px-4 py-1 text-[11px] font-semibold rounded bg-white/20 backdrop-blur"
              >
                {c}
              </motion.div>
            ))}
          </motion.div>

          {/* HEADING */}
          <motion.h1
  variants={headingVariants}
  className="text-[34px] sm:text-[40px] lg:text-[48px] font-extrabold leading-[1.15] 
             max-w-[540px] mx-auto lg:mx-0 text-black lg:text-white"
>
  Launch Your Global
  <br /> Education Journey
</motion.h1>


          {/* DESCRIPTION */}
          <motion.p
  variants={textVariants}
  className="mt-4 text-[14px] sm:text-[15px] max-w-[500px] mx-auto lg:mx-0 
             text-black/80 lg:text-white/90"
>
  Expert guidance for study abroad admissions, visas, and university selection.
  Start your international education journey with confidence and clarity.
</motion.p>


          {/* ADDRESS */}
          <motion.div
            variants={addressVariants}
            className="mt-8 bg-white px-6 py-5 rounded-xl w-full sm:w-[380px] text-[14px] leading-relaxed shadow-md mx-auto lg:mx-0"
          >
            <p className="font-semibold text-blue-900">
              Good Will Global Solutions

            </p>
            <p className="font-semibold text-blue-900 mt-1">
              H No. 3-1/145, 5th floor, Neknampur, Gandipet, Manikonda,K.V. Rangareddy dist. 500089
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* PLANE */}
      <motion.div
        variants={planeVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="hidden lg:block absolute top-16 right-20 text-white/70 text-2xl"
      >
        ✈
      </motion.div>
    </section>
  );
}
